import React, { useState } from 'react';

import TruvBridge from '@truv/react';

import { LoadingType } from 'prequalificationV2/actions';
import { truvApi, VerificationState } from 'api/TruvApi';
import BlurPopup from 'components/BlurPopup';
import InteractiveBox from 'components/InteractiveBox';
import { InteractiveBoxStatus } from 'components/InteractiveBox/InteractiveBox';
import Section from 'prequalificationV2/components/LoanPortal/Sections/Section';
import { BorrowerRole } from 'enums/BorrowerRole';
import { ApplicationProductName } from 'enums/common.enums';
import { DocumentCategory } from 'enums/DocumentCategory';
import { IncomeType } from 'enums/IncomeType';
import { KbaStatus } from 'enums/ApplicationsVariables';
import { SocialSecurityBenefitType } from 'enums/SocialSecurityBenefitType';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { TRUV_DISABLED_STATUS } from 'prequalificationV2/utils/constants';
import UploadDocumentsCategory from 'prequalificationV2/components/LoanPortal/UploadDocuments/UploadDocumentCategory';
import uploadDocumentsStyles from 'prequalificationV2/components/LoanPortal/UploadDocuments/UploadDocuments.module.scss';
import {
  getDocumentsByCategory,
  isDocumentCategoryCompleted,
  isDocumentCategoryRequired,
} from 'prequalificationV2/utils/documentsUtils';
import { DashboardTabName } from 'enums/DashboardTabName';
import Loader from 'components/Loader';
import SubSection, { SubSectionState } from 'prequalificationV2/components/LoanPortal/Sections/SubSection/SubSection';
import KbaPopup from 'prequalificationV2/pages/Dashboard/Identity/KbaPopup';
import { setVerificationState } from 'prequalificationV2/actions/dashboardPortal';
import useIncomeWaterfallSocket from 'prequalificationV2/hooks/useIncomeWaterfallSocket';

import styles from '../InformationTabs.module.scss';
import BorrowerIncomeInfo from './BorrowerIncomeInfo/BorrowerIncomeInfo';
import { getIncomeInformationCompleteVariableName } from './BorrowerIncomeInfo/borrowerIncomeInfo.utils';
import EmploymentInformationBlock from './SmallLoanProgram/EmploymentInformationBlock/EmploymentInformationBlock';

interface BorrowerInformationTabProps {
  borrowerType: BorrowerRole;
}

const COMPANY_NAME_MAX_LENGTH = 15;

const BorrowerInformationTab = ({ borrowerType }: BorrowerInformationTabProps) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) =>
      state.loading[
        `${LoadingType.RequiredInformationUpdating}${
          borrowerType === BorrowerRole.Borrower
            ? DashboardTabName.BorrowerInformation
            : DashboardTabName.CoBorrowerInformation
        }`
      ],
  );
  const [isVerificationInProgress, setIsVerificationInProgress] = useState<boolean>(false);
  const [isVerificationWidgetOpened, setIsVerificationWidgetOpened] = useState<boolean>(false);
  const [isSsaPopupOpened, setIsSsaPopupOpened] = useState<boolean>(false);
  const [currentToken, setCurrentToken] = useState<string>('');
  const [isKbaPopupOpened, setIsKbaPopupOpened] = useState(false);
  const [currentVerificationId, setCurrentVerificationId] = useState<string>('');
  const { applicationId, productName, variables: applicationVariables } = useAppSelector((state) => state.application);
  const isIncomeInformationComplete = Boolean(
    applicationVariables?.[getIncomeInformationCompleteVariableName(borrowerType) as keyof typeof applicationVariables],
  );
  const { firstName: borrowerFirstName, borrowerId } = useAppSelector((state) => state.borrower);
  const { firstName: coBorrowerFirstName } = useAppSelector((state) => state.coBorrower);
  const { requiredDocuments } = useAppSelector((state) => state.documents);
  const {
    borrowerPayrollProviderIsDisabled,
    coBorrowerPayrollProviderIsDisabled,
    payrollProviderBorrowerVerificationStates,
    payrollProviderCoBorrowerVerificationStates,
    borrowerEmploymentInformationComplete,
    coBorrowerEmploymentInformationComplete,
  } = useAppSelector((state) => state.dashboardPortal);
  const isBorrower = borrowerType === BorrowerRole.Borrower;
  const payrollProviderIsDisabled = isBorrower
    ? borrowerPayrollProviderIsDisabled
    : coBorrowerPayrollProviderIsDisabled;
  const isTruvDisabled = process.env.REACT_APP_TRUV_STATUS === TRUV_DISABLED_STATUS;
  const payrollProviderVerificationStates = isBorrower
    ? payrollProviderBorrowerVerificationStates
    : payrollProviderCoBorrowerVerificationStates;

  const verificationOfIncomeRequired = payrollProviderVerificationStates.some(
    (verification) => !!verification.verificationRequired,
  );

  const incomeCategory = isBorrower ? DocumentCategory.BorrowerIncome : DocumentCategory.CoborrowerIncome;
  const idCategory = isBorrower ? DocumentCategory.BorrowerID : DocumentCategory.CoborrowerID;
  const otherCategory = isBorrower ? DocumentCategory.BorrowerOther : DocumentCategory.CoborrowerOther;

  const getBridgeTokenValue = async (
    verificationId: string | null,
    incomeType: IncomeType,
    socialSecurityBenefitType: SocialSecurityBenefitType | null,
  ) => {
    try {
      setIsVerificationInProgress(true);

      const incomeSourceVerificationId =
        incomeType === IncomeType.SocialSecurity
          ? payrollProviderVerificationStates.find(
              (verification) => verification.incomeType === IncomeType.SocialSecurity,
            )!.id
          : verificationId;

      const { token } = await truvApi.createBridgeToken({
        borrowerId: borrowerId as string,
        applicationId: applicationId as string,
        borrowerType: isBorrower ? BorrowerRole.Borrower : BorrowerRole.CoBorrower,
        incomeSourceVerificationId: incomeSourceVerificationId!,
        socialSecurityBenefitType: socialSecurityBenefitType,
      });

      setCurrentToken(token);
      setIsVerificationWidgetOpened(true);
      setCurrentVerificationId(incomeSourceVerificationId!);
      setIsSsaPopupOpened(false);
      setIsVerificationInProgress(false);
    } catch (error) {
      setIsVerificationWidgetOpened(false);
      setIsSsaPopupOpened(false);
      setIsVerificationInProgress(false);
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    }
  };

  const handleSuccessVerification = async (taskId: string) => {
    try {
      setIsVerificationWidgetOpened(false);

      dispatch(setVerificationState({ verificationId: currentVerificationId, isBorrower: isBorrower }));

      await truvApi.completeVerification({
        applicationId: applicationId!,
        verificationId: currentVerificationId,
        taskId: taskId,
        borrowerType: isBorrower ? BorrowerRole.Borrower : BorrowerRole.CoBorrower,
      });
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    }
  };

  const truncateW2CompanyName = (companyName: string) => {
    if (companyName.length > COMPANY_NAME_MAX_LENGTH) {
      return companyName.substring(0, COMPANY_NAME_MAX_LENGTH) + '...';
    }

    return companyName;
  };

  const handleSSAPopupSubmit = (socialSecurityBenefitType: SocialSecurityBenefitType) => {
    return isVerificationInProgress
      ? () => {}
      : () => getBridgeTokenValue(null, IncomeType.SocialSecurity, socialSecurityBenefitType);
  };

  const handleSSAPopupClose = () => {
    setIsSsaPopupOpened(false);
  };

  const getVerificationBoxClickHandler = (state: VerificationState) => () => {
    if (isVerificationInProgress || state.verificationAttemptComplete) {
      return;
    }

    if (state.incomeType === IncomeType.SocialSecurity) {
      return setIsSsaPopupOpened(true);
    }

    return getBridgeTokenValue(state.id, state.incomeType, null);
  };

  const checkEmploymentInformationBlockIsVisible = () => {
    return (
      productName === ApplicationProductName.SmallLoanProgram &&
      isIncomeInformationComplete &&
      (isBorrower
        ? !applicationVariables?.bor_proof_of_income_required
        : !applicationVariables?.cob_proof_of_income_required)
    );
  };

  const isIncomeWaterfallApplied =
    applicationVariables?.income_waterfall_applied || applicationVariables?.income_waterfall_completed;

  const isIncomeWaterfallPayrollProviderVisible =
    borrowerType === BorrowerRole.Borrower
      ? applicationVariables?.borrower_income_waterfall_payroll_provider_visible
      : applicationVariables?.coborrower_income_waterfall_payroll_provider_visible;

  const isIncomeWaterfallDocUploadVisible =
    borrowerType === BorrowerRole.Borrower
      ? applicationVariables?.borrower_income_waterfall_doc_upload_visible
      : applicationVariables?.coborrower_income_waterfall_doc_upload_visible;

  const isIncomeWaterfallExperianVisible =
    borrowerType === BorrowerRole.Borrower
      ? applicationVariables?.borrower_income_waterfall_experian_visible
      : applicationVariables?.coborrower_income_waterfall_experian_visible;

  const IncomeVerificationSubSectionState: SubSectionState = {
    isExist:
      !isTruvDisabled &&
      !payrollProviderIsDisabled &&
      !!payrollProviderVerificationStates.length &&
      verificationOfIncomeRequired &&
      (!isIncomeWaterfallApplied || !!isIncomeWaterfallPayrollProviderVisible),
    isCompleted: payrollProviderVerificationStates.every((verification) =>
      verification.verificationRequired ? verification.verificationAttemptComplete : true,
    ),
  };

  const IncomeDocumentsSubSectionState: SubSectionState = {
    isExist:
      isDocumentCategoryRequired(requiredDocuments, incomeCategory) &&
      (!isIncomeWaterfallApplied || !!isIncomeWaterfallDocUploadVisible),
    isCompleted: isDocumentCategoryCompleted(requiredDocuments, incomeCategory),
  };

  useIncomeWaterfallSocket(applicationId!, borrowerType, borrowerId!, localStorage.getItem('token'));

  const isKbaRequired = isBorrower
    ? applicationVariables?.borrower_kba_required
    : applicationVariables?.coborrower_kba_required;
  const kbaStatus = isBorrower
    ? applicationVariables?.borrower_kba_status
    : applicationVariables?.coborrower_kba_status;
  const isKbaAvailable = ![KbaStatus.Unavailable, KbaStatus.Expired].includes(kbaStatus!);
  const isKbaCompleted = [KbaStatus.Passed, KbaStatus.Failed].includes(kbaStatus!);

  const kbaSubSectionState: SubSectionState = {
    isExist: !!isKbaRequired && isKbaAvailable,
    isCompleted: isKbaCompleted,
  };

  return (
    <>
      {loading || isIncomeWaterfallExperianVisible ? (
        <Loader />
      ) : (
        <div className={styles.requiredInformationContainer}>
          <BorrowerIncomeInfo borrowerType={borrowerType} isIncomeInformationComplete={isIncomeInformationComplete} />
          {checkEmploymentInformationBlockIsVisible() && (
            <Section
              title={'Employment'}
              subSectionStates={[
                {
                  isExist: true,
                  isCompleted: isBorrower
                    ? borrowerEmploymentInformationComplete
                    : coBorrowerEmploymentInformationComplete,
                },
              ]}
            >
              <EmploymentInformationBlock borrowerRole={borrowerType} />
            </Section>
          )}
          <Section
            title={'Income'}
            subSectionStates={[IncomeVerificationSubSectionState, IncomeDocumentsSubSectionState]}
          >
            <SubSection state={IncomeVerificationSubSectionState}>
              <div>
                <div className={styles.sectionHeader}>Income Source Login</div>
                <div className={styles.sectionTitle}>
                  Instant Income Verification - You will enter your username and password for your employer, payroll
                  provider, and/or benefits provider. That's it.
                </div>
                <div className={styles.verificationContainer}>
                  {payrollProviderVerificationStates.map((state) => (
                    <div key={state.id} className={styles.verificationStateBoxesWrapper}>
                      {state.verificationRequired && (
                        <div className={styles.verificationBox}>
                          <InteractiveBox
                            asyncLoading
                            status={
                              state.verificationAttemptComplete
                                ? InteractiveBoxStatus.Completed
                                : InteractiveBoxStatus.Initial
                            }
                            title={
                              <>
                                {isBorrower ? borrowerFirstName : coBorrowerFirstName}'s {state.incomeType}
                                <div className={styles.verificationSubtitle}>
                                  {state.incomeType === IncomeType.W2 ? truncateW2CompanyName(state.companyName) : ''}
                                </div>
                              </>
                            }
                            onClick={getVerificationBoxClickHandler(state)}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <TruvBridge
                  bridgeToken={currentToken}
                  onClose={() => setIsVerificationWidgetOpened(false)}
                  onSuccess={(_, taskId: any) => handleSuccessVerification(taskId.task_id)}
                  isOpened={isVerificationWidgetOpened}
                />
              </div>
            </SubSection>
            <SubSection state={IncomeDocumentsSubSectionState}>
              <UploadDocumentsCategory
                title={IncomeVerificationSubSectionState.isExist ? 'Documents Upload' : ''}
                documents={getDocumentsByCategory(requiredDocuments, incomeCategory)}
                category={incomeCategory}
              />
            </SubSection>
          </Section>
          <Section
            title={'ID'}
            subSectionStates={[
              {
                isExist: isDocumentCategoryRequired(requiredDocuments, idCategory),
                isCompleted: isDocumentCategoryCompleted(requiredDocuments, idCategory),
              },
              kbaSubSectionState,
            ]}
          >
            <UploadDocumentsCategory
              documents={getDocumentsByCategory(requiredDocuments, idCategory)}
              category={idCategory}
            >
              {kbaSubSectionState.isExist && (
                <div className={uploadDocumentsStyles.categoryItem}>
                  <InteractiveBox
                    asyncLoading
                    status={isKbaCompleted ? InteractiveBoxStatus.Completed : InteractiveBoxStatus.Initial}
                    title={
                      <>{isBorrower ? borrowerFirstName : coBorrowerFirstName}'s Identity Verification Questions</>
                    }
                    onClick={() => {
                      if (!isKbaCompleted) {
                        setIsKbaPopupOpened(true);
                      }
                    }}
                  />
                </div>
              )}
            </UploadDocumentsCategory>
            <KbaPopup isOpen={isKbaPopupOpened} setOpen={setIsKbaPopupOpened} isBorrower={isBorrower} />
          </Section>
          <Section
            title={'Other'}
            subSectionStates={[
              {
                isExist: isDocumentCategoryRequired(requiredDocuments, otherCategory),
                isCompleted: isDocumentCategoryCompleted(requiredDocuments, otherCategory),
              },
            ]}
          >
            <UploadDocumentsCategory
              documents={getDocumentsByCategory(requiredDocuments, otherCategory)}
              category={otherCategory}
            />
          </Section>
          <BlurPopup
            id="popup"
            onClose={handleSSAPopupClose}
            open={isSsaPopupOpened}
            scroll="body"
            blurDeep={8}
            isCloseButtonDisplayed
          >
            <span className={styles.popupTitle}>
              Please select the type of Social Security income you want to verify
            </span>
            <div className={styles.verificationBoxes}>
              <InteractiveBox
                asyncLoading
                status={InteractiveBoxStatus.Initial}
                title={`${isBorrower ? borrowerFirstName : coBorrowerFirstName}'s Retirement Benefits`}
                boxClassName={styles.smallVerificationBox}
                onClick={handleSSAPopupSubmit(SocialSecurityBenefitType.Retirement)}
              />
              <InteractiveBox
                asyncLoading
                status={InteractiveBoxStatus.Initial}
                title={`${isBorrower ? borrowerFirstName : coBorrowerFirstName}'s Disability Benefits`}
                boxClassName={styles.smallVerificationBox}
                onClick={handleSSAPopupSubmit(SocialSecurityBenefitType.Disability)}
              />
            </div>
          </BlurPopup>
        </div>
      )}
    </>
  );
};

export default BorrowerInformationTab;
